import { Box, Button, Dialog, Select, Text, TextField } from '@radix-ui/themes';
import React, { useEffect, useState } from 'react';
import { CreatePOSDeviceRequest } from '../../model/POS-device.model';
import BankService from '../../service/bank-service/Bank.service';
import POSDeviceService from '../../service/pos-device-service/POS-device.service';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

interface CreatePOSDeviceModalProps {
  onSuccess: () => Promise<void>;
}

const CreatePOSDeviceModal: React.FC<CreatePOSDeviceModalProps> = ({
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreatePOSDeviceRequest>({
    deviceCode: '',
    location: '',
    status: 'ACTIVE',
    bankId: 0,
  });
  const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const screens = useBreakpoint();

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const bankList = await BankService.getBankList();
        setBanks(bankList);
      } catch (error) {
        console.error('Error fetching bank list:', error);
      }
    };

    fetchBanks();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await POSDeviceService.createPOSDevice(formData);
      onSuccess();
      setFormData({
        deviceCode: '',
        location: '',
        status: 'ACTIVE',
        bankId: 0,
      });
      setOpen(false);
    } catch (error) {
      console.error('Error creating POS device:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'bankId' ? Number(value) : value,
    }));
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button
          color="indigo"
          variant="soft"
          className={`cursor-pointer ${screens.xs ? 'w-full' : ''}`}
        >
          Tạo thiết bị POS mới
        </Button>
      </Dialog.Trigger>

      <Dialog.Content style={{ maxWidth: screens.xs ? '95%' : 700 }}>
        <Dialog.Title>Tạo thiết bị POS</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Điền thông tin thiết bị
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Box className="space-y-4">
            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Mã thiết bị <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                name="deviceCode"
                placeholder="Nhập mã thiết bị"
                value={formData.deviceCode}
                onChange={handleChange}
                required
                className="w-full"
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Địa điểm
              </Text>
              <TextField.Root
                name="location"
                placeholder="Nhập địa điểm"
                value={formData.location}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Text as="label" size="2" mb="1" weight="bold">
                Ngân hàng <span className="text-red-500">*</span>
              </Text>
              <Select.Root
                name="bankId"
                value={formData.bankId.toString()}
                onValueChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    bankId: Number(value),
                  }));
                }}
                required
              >
                <Select.Trigger
                  placeholder="Chọn ngân hàng"
                  className="w-full cursor-pointer"
                />
                <Select.Content position="popper">
                  <Select.Group>
                    {banks.map((bank) => (
                      <Select.Item key={bank.id} value={bank.id.toString()}>
                        {bank.name}
                      </Select.Item>
                    ))}
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </div>

            <div className="flex flex-col gap-1">
              <Text as="label" size="2" weight="bold">
                Trạng thái
              </Text>
              <Select.Root
                name="status"
                value={formData.status}
                onValueChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    status: value,
                  }));
                }}
              >
                <Select.Trigger
                  placeholder="Tình trạng"
                  className="w-full cursor-pointer"
                />
                <Select.Content position="popper">
                  <Select.Group>
                    <Select.Item value="ACTIVE">Hoạt động</Select.Item>
                    <Select.Item value="INACTIVE">Không hoạt động</Select.Item>
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </div>
          </Box>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                className="cursor-pointer w-full sm:w-auto"
              >
                Hủy
              </Button>
            </Dialog.Close>
            <Button
              color="indigo"
              variant="soft"
              type="submit"
              className="cursor-pointer w-full sm:w-auto"
            >
              Hoàn thành
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default CreatePOSDeviceModal;
